import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { ContactStageService, ContactStage } from '../shared/services/contact-stage.service';
import { PermissionService } from '../shared/services/permission.service';
import { AuthService, UserRole } from '../auth/auth.service';
import { RCOversightService, CompanyDropdownOption } from '../services/rc-oversight.service';
import { RCDataSyncService } from '../services/rc-data-sync.service';
import { config } from '../config';

@Component({
  selector: 'app-stage-management',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="stage-management">
      <div class="header">
        <div class="header-title">
          <h2>Contact Stages</h2>
        </div>
        <div class="header-actions">
          <button
            *ngIf="canManageStages"
            class="schedule-btn"
            (click)="openCreateModal()">
            <span>+</span> Add New Stage
          </button>
        </div>
      </div>

      <div class="stage-list" *ngIf="stages.length > 0">
        <div
          *ngFor="let stage of stages; let i = index; trackBy: trackByStageId"
          class="stage-card">

          <!-- Left: drag handle + order badge -->
          <div class="card-left">
            <div class="drag-handle">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="9" cy="5"  r="1.5"/><circle cx="15" cy="5"  r="1.5"/>
                <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
                <circle cx="9" cy="19" r="1.5"/><circle cx="15" cy="19" r="1.5"/>
              </svg>
            </div>
          </div>

          <!-- Middle: info -->
          <div class="card-body">
            <div class="card-title-row">
              <span class="stage-name">{{ stage.name }}</span>
              <span *ngIf="stage.company?.name" class="company-tag">{{ stage.company?.name }}</span>
            </div>
            <div class="stage-description" *ngIf="stage.description">{{ stage.description }}</div>
            <div class="stage-meta">
              <span *ngIf="stage.last_modified_by && stage.updatedAt !== stage.createdAt">
                Modified {{ formatDate(stage.updatedAt) }}
              </span>
              <span *ngIf="!stage.last_modified_by || stage.updatedAt === stage.createdAt">
                Created {{ formatDate(stage.createdAt) }}
              </span>
            </div>
          </div>

          <!-- Right: icon actions -->
          <div class="card-actions" *ngIf="canManageStages">
            <button class="icon-btn" (click)="editStage(stage)" title="Edit Stage">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
              </svg>
            </button>
            <button class="icon-btn delete-icon-btn" (click)="confirmDeleteStage(stage)" title="Delete Stage">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
              </svg>
            </button>
          </div>

        </div>
      </div>

      <div class="empty-state" *ngIf="stages.length === 0 && !loading">
        <h3>No Contact Stages</h3>
        <p>No contact stages have been created yet.</p>
        <button
          *ngIf="canManageStages"
          class="schedule-btn"
          style="margin: 0 auto;"
          (click)="openCreateModal()">
          <span>+</span> Create First Stage
        </button>
      </div>

      <div class="loading" *ngIf="loading">
        <span class="ap-spinner"></span>
        <p>Loading stages...</p>
      </div>

      <!-- Create/Edit Modal -->
      <div class="modal-overlay" *ngIf="modalMode">
        <div class="modal">
          <div class="modal-header">
            <h3>{{ modalMode === 'create' ? 'Create New Stage' : 'Edit Stage' }}</h3>
            <button class="close-btn" (click)="closeModal()">&times;</button>
          </div>
          <div class="modal-body">
            <form (ngSubmit)="saveStage()" #stageForm="ngForm">
              <div class="form-group">
                <label for="name">Stage Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  [(ngModel)]="currentStage.name"
                  required
                  maxlength="100"
                  class="form-control">
              </div>
              <div class="form-group">
                <label for="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  [(ngModel)]="currentStage.description"
                  maxlength="500"
                  rows="3"
                  class="form-control">
                </textarea>
              </div>
              <div class="form-group">
                <label for="order">Order <span class="optional">(Optional)</span></label>
                <input
                  type="number"
                  id="order"
                  name="order"
                  [(ngModel)]="currentStage.order"
                  min="0"
                  class="form-control"
                  placeholder="Leave empty for auto-ordering">
                <small class="help-text">Leave empty for auto-ordering, or enter number (0, 1, 2...) for custom order</small>
              </div>
              <div class="form-group" *ngIf="isSuperAdmin()">
                <label for="mcCompany">Company *</label>
                <select
                  id="mcCompany"
                  name="mcCompany"
                  [(ngModel)]="currentStage.mcCompanyId"
                  class="form-control"
                  required>
                  <option value="">Select Company</option>
                  <option
                    *ngFor="let company of mcCompanies"
                    [value]="company.id">
                    {{ company.name }}
                  </option>
                </select>
                <small class="help-text">Select which company this stage applies to</small>
              </div>
              <div class="modal-actions">
                <button
                  type="button"
                  class="btn-secondary"
                  (click)="closeModal()">
                  Cancel
                </button>
                <button
                  type="submit"
                  class="btn-primary"
                  [disabled]="!stageForm.valid || saving">
                  {{ saving ? 'Saving...' : (modalMode === 'create' ? 'Create' : 'Update') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Delete Confirmation Modal -->
      <div class="modal-overlay" *ngIf="showDeleteModal">
        <div class="modal delete-modal">
          <div class="modal-header">
            <h3>Confirm Delete</h3>
            <button class="close-btn" (click)="showDeleteModal = false">&times;</button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to delete the stage "{{ stageToDelete?.name }}"?</p>
            <p class="warning">This action cannot be undone.</p>
            <div class="modal-actions">
              <button 
                type="button" 
                class="btn-secondary"
                (click)="showDeleteModal = false">
                Cancel
              </button>
              <button 
                type="button" 
                class="btn-danger"
                (click)="deleteStage()"
                [disabled]="deleting">
                {{ deleting ? 'Deleting...' : 'Delete' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Toast Messages -->
      <div class="toast" *ngIf="toastMessage" [class.success]="toastType === 'success'" [class.error]="toastType === 'error'">
        {{ toastMessage }}
      </div>
    </div>
  `,
  styles: [`
    .stage-management {
      padding: 10px;
      width: 100%;
      box-sizing: border-box;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 20px;
      flex-wrap: wrap;
      gap: 10px;
    }

    .header-title h2 {
      margin: 0 0 2px 0;
      font-size: 1.35rem;
      font-weight: 700;
      color: var(--ap-text, #0B1220);
    }

    .header-actions {
      display: flex;
      gap: 8px;
      align-items: center;
      flex-wrap: wrap;
    }

    .schedule-btn {
      display: flex;
      align-items: center;
      gap: 5px;
      background: var(--ap-primary, #2F5BDA);
      color: #fff;
      border: none;
      height: 34px;
      padding: 0 16px;
      border-radius: var(--ap-radius-sm, 6px);
      cursor: pointer;
      font-size: 13px;
      font-weight: 600;
    }

    .schedule-btn:hover {
      background: #2449b8;
    }

    .stage-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .stage-card {
      background: var(--ap-surface, #fff);
      border: 1px solid var(--ap-border, #E3E6EB);
      border-radius: var(--ap-radius, 10px);
      padding: 16px 20px;
      display: flex;
      align-items: center;
      gap: 14px;
    }

    /* Left: drag handle + order badge */
    .card-left {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
    }

    .drag-handle {
      color: var(--ap-text-muted, #9CA3AF);
      cursor: grab;
      display: flex;
      align-items: center;
      padding: 4px;
      border-radius: 4px;
      background: #E3E6EB;
    }

    .drag-handle:hover {
      color: var(--ap-text-soft, #4B5563);
      background: #d0d4da;
    }


    /* Middle: info */
    .card-body {
      flex: 1;
      min-width: 0;
    }

    .card-title-row {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
      margin-bottom: 4px;
    }

    .stage-name {
      font-size: 14px;
      font-weight: 600;
      color: var(--ap-text, #0B1220);
    }

    .company-tag {
      background: #E3E6EB;
      color: var(--ap-text-muted, #6B7280);
      border: 1px solid #E3E6EB;
      padding: 2px 7px;
      border-radius: var(--ap-radius-sm, 6px);
      font-size: 10px;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.4px;
    }

    .stage-description {
      font-size: 13px;
      color: var(--ap-text-soft, #4B5563);
      margin-bottom: 3px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .stage-meta {
      font-size: 11px;
      color: var(--ap-text-muted, #9CA3AF);
    }

    /* Right: icon-only action buttons */
    .card-actions {
      display: flex;
      gap: 6px;
      align-items: center;
      flex-shrink: 0;
    }

    .icon-btn {
      background: none;
      border: none;
      padding: 6px;
      border-radius: var(--ap-radius-sm, 6px);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--ap-text-muted, #9CA3AF);
      transition: background 0.15s, color 0.15s;
    }

    .icon-btn:hover {
      background: var(--ap-surface-muted, #EEF0F3);
      color: var(--ap-text-soft, #4B5563);
    }

    .delete-icon-btn:hover {
      background: var(--ap-danger-soft, #FEE2E2);
      color: var(--ap-danger, #B42318);
    }

    .empty-state {
      text-align: center;
      padding: 60px 20px;
      color: var(--ap-text-muted, #6B7280);
    }

    .empty-state h3 {
      margin-bottom: 12px;
      color: var(--ap-text, #0B1220);
    }

    .loading {
      text-align: center;
      padding: 40px;
      color: var(--ap-text-muted, #6B7280);
    }

    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(13,18,30,.45);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1000;
    }

    .modal {
      background: var(--ap-surface, #fff);
      border-radius: var(--ap-radius-lg, 14px);
      width: 90%;
      max-width: 500px;
      max-height: 90vh;
      overflow-y: auto;
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 20px;
      border-bottom: 1px solid var(--ap-border, #E3E6EB);
    }

    .modal-header h3 {
      margin: 0;
      color: var(--ap-text, #0B1220);
      font-size: 15px;
      font-weight: 600;
      line-height: 1.4;
    }

    .close-btn {
      background: none;
      border: none;
      width: 28px;
      height: 28px;
      border-radius: var(--ap-radius-sm, 6px);
      font-size: 18px;
      cursor: pointer;
      color: var(--ap-text-muted, #6B7280);
      padding: 0;
      margin: 0;
      line-height: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.15s;
    }

    .close-btn:hover {
      background: var(--ap-surface-muted, #EEF0F3);
      color: var(--ap-text, #0B1220);
    }

    .modal-body {
      padding: 16px 20px 20px;
    }

    .form-group {
      margin-bottom: 16px;
    }

    .form-group label {
      display: block;
      margin-bottom: 5px;
      color: var(--ap-text-soft, #4B5563);
      font-weight: 550;
      font-size: 12px;
    }

    .form-control {
      width: 100%;
      padding: 7px 10px;
      border: 1px solid var(--ap-border, #E3E6EB);
      border-radius: var(--ap-radius-sm, 6px);
      font-size: 13px;
      background: var(--ap-surface, #fff);
      color: var(--ap-text, #0B1220);
      box-sizing: border-box;
    }

    .form-control:focus {
      outline: none;
      border-color: var(--ap-primary, #2F5BDA);
      box-shadow: var(--ap-ring, 0 0 0 3px rgba(47,91,218,.18));
    }

    .help-text {
      font-size: 11px;
      color: var(--ap-text-muted, #6B7280);
      margin-top: 4px;
      font-style: italic;
    }

    .modal-actions {
      display: flex;
      gap: 8px;
      justify-content: flex-end;
      margin-top: 16px;
    }

    .delete-modal .warning {
      color: var(--ap-danger, #B42318);
      font-size: 0.9rem;
    }

    .btn-primary, .btn-secondary, .btn-danger {
      padding: 0 14px;
      height: 32px;
      border: 1px solid transparent;
      border-radius: var(--ap-radius-sm, 6px);
      cursor: pointer;
      font-size: 13px;
      font-weight: 550;
      white-space: nowrap;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 72px;
      margin: 0;
      transition: background 0.15s, border-color 0.15s;
    }

    .modal-actions .btn-primary,
    .modal-actions .btn-secondary {
      min-width: 80px;
    }

    .btn-primary {
      background: var(--ap-primary, #2F5BDA);
      color: #fff;
      border-color: var(--ap-primary, #2F5BDA);
    }

    .btn-secondary {
      background: var(--ap-surface-muted, #EEF0F3);
      color: var(--ap-text-soft, #4B5563);
      border-color: var(--ap-border, #E3E6EB);
    }

    .btn-secondary:hover:not(:disabled) {
      background: var(--ap-border, #E3E6EB);
    }

    .btn-primary:hover:not(:disabled) {
      background: var(--ap-primary-ink, #1E3FA8);
      border-color: var(--ap-primary-ink, #1E3FA8);
    }

    .btn-danger {
      background: var(--ap-danger, #B42318);
      color: #fff;
      border-color: var(--ap-danger, #B42318);
    }

    .btn-danger:hover:not(:disabled) {
      background: #922B21;
      border-color: #922B21;
    }

    button:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .toast {
      position: fixed;
      bottom: 20px;
      right: 20px;
      padding: 10px 18px;
      border-radius: var(--ap-radius-sm, 6px);
      color: #fff;
      z-index: 1001;
      font-size: 13px;
      font-weight: 500;
      animation: slideIn 0.3s ease;
    }

    .toast.success {
      background: var(--ap-success, #047A55);
    }

    .toast.error {
      background: var(--ap-danger, #B42318);
    }

    @keyframes slideIn {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    @media (max-width: 768px) {
      .stage-management {
        padding: 10px;
      }

      .header {
        flex-direction: column;
        align-items: stretch;
      }

      .header-actions {
        justify-content: flex-start;
        flex-wrap: wrap;
        gap: 8px;
      }

      .stage-card {
        padding: 12px 14px;
        gap: 10px;
      }

      .stage-name {
        font-size: 13px;
      }

      .modal-overlay {
        padding: 16px;
        align-items: center;
      }

      .modal {
        width: 100%;
        margin: 0;
      }

      .modal-header h3 {
        font-size: 14px;
      }

      .form-group label {
        font-size: 12px;
      }

      .form-control {
        font-size: 13px;
      }

      .help-text {
        font-size: 11px;
      }
    }

    @media (max-width: 480px) {
      .stage-management {
        padding: 10px;
      }

      .header-title h2 {
        font-size: 1rem;
      }

      .stage-card {
        padding: 10px 12px;
        gap: 8px;
      }

.stage-description {
        font-size: 12px;
      }

      .company-tag {
        font-size: 9px;
        padding: 2px 5px;
      }

      .modal-overlay {
        padding: 12px;
      }

      .modal {
        width: 100%;
        margin: 0;
      }

      .modal-header h3 {
        font-size: 13px;
      }

      .form-group label {
        font-size: 12px;
      }

      .form-control {
        font-size: 12px;
        padding: 7px 10px;
      }

      .help-text {
        font-size: 10px;
      }
    }

    .optional {
      color: var(--ap-text-muted, #6B7280);
      margin-left: 4px;
      font-size: 12px;
      font-weight: normal;
    }
  `]
})
export class StageManagementComponent implements OnInit, OnDestroy {
  stages: ContactStage[] = [];
  loading = false;
  saving = false;
  deleting = false;
  canManageStages = false;
  mcCompanies: CompanyDropdownOption[] = [];
  loadingCompanies = false;
  
  modalMode: 'create' | 'edit' | null = null;
  showDeleteModal = false;
  
  currentStage: any = {};
  stageToDelete: ContactStage | null = null;
  
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';
  
  private subscriptions = new Subscription();

  constructor(
    private stageService: ContactStageService,
    private permissionService: PermissionService,
    private authService: AuthService,
    private rcOversightService: RCOversightService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private rcDataSyncService: RCDataSyncService
  ) {}

  ngOnInit() {
    this.checkPermissions();
    this.loadStages();
    this.loadMCCompanies();
    
    this.subscriptions.add(
      this.stageService.stages$.subscribe(stages => {
        this.stages = stages.sort((a, b) => {
          // First sort by creation date (latest first)
          const dateA = new Date(a.createdAt || 0).getTime();
          const dateB = new Date(b.createdAt || 0).getTime();
          return dateB - dateA; // Descending order (latest first)
        });
      })
    );

    // Add RC data sync subscription
    if (this.isRCSuperAdmin()) {
      this.subscriptions.add(
        this.rcDataSyncService.stageUpdates$.subscribe(
          (event) => {
            console.log('[StageManagementComponent] RC data sync event received:', event);
            if (event.event === 'created' || event.event === 'updated' || event.event === 'deleted') {
              console.log('[StageManagementComponent] Refreshing stages due to RC sync event');
              this.loadStages();
            }
          }
        )
      );
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  checkPermissions() {
    const currentUser = this.authService.currentUserValue;
    this.canManageStages = this.permissionService.canManageStages(currentUser?.role);
  }

  loadStages() {
    this.loading = true;
    
    // Check if user is RC (Super Admin) and should load MC stages
    if (this.isRCUser()) {
      console.log('[StageManagement] Loading stages for RC user');
      this.loadRCStages();
    } else {
      console.log('[StageManagement] Loading stages for regular user');
      this.loadRegularStages();
    }
  }

  private loadRegularStages() {
    this.subscriptions.add(
      this.stageService.getStages().subscribe({
        next: () => {
          this.loading = false;
        },
        error: (error) => {
          this.loading = false;
          this.showToast('Failed to load stages', 'error');
          console.error('Error loading stages:', error);
        }
      })
    );
  }

  private loadRCStages() {
    this.subscriptions.add(
      this.rcOversightService.getMCStages().subscribe({
        next: (response) => {
          if (response.success) {
            // Update the stage service with RC oversight stages and sort by latest first
            this.stages = (response.data || []).sort((a: ContactStage, b: ContactStage) => {
              // Sort by creation date (latest first)
              const dateA = new Date(a.createdAt || 0).getTime();
              const dateB = new Date(b.createdAt || 0).getTime();
              return dateB - dateA; // Descending order (latest first)
            });
            console.log('[StageManagement] Loaded RC oversight stages:', this.stages.length);
          } else {
            this.showToast('Failed to load RC stages', 'error');
          }
          this.loading = false;
        },
        error: (error) => {
          this.loading = false;
          this.showToast('Failed to load RC stages', 'error');
          console.error('Error loading RC stages:', error);
        }
      })
    );
  }

  editStage(stage: ContactStage) {
    this.currentStage = {
      id: stage._id,
      name: stage.name,
      description: stage.description || '',
      order: stage.order || 0,
      mcCompanyId: (stage as any).mcCompanyId || ''
    };
    this.modalMode = 'edit';
  }

  confirmDeleteStage(stage: ContactStage) {
    this.stageToDelete = stage;
    this.showDeleteModal = true;
  }

  saveStage() {
    this.saving = true;
    
    const stageData: any = {
      name: this.currentStage.name,
      description: this.currentStage.description
    };
    
    // Only include order if it's explicitly set (not undefined/null/empty)
    if (this.currentStage.order !== undefined && this.currentStage.order !== null && this.currentStage.order !== '') {
      stageData.order = this.currentStage.order;
    }
    
    // Include MC company ID if Super Admin and value is selected
    if (this.isSuperAdmin() && this.currentStage.mcCompanyId) {
      stageData.mcCompanyId = this.currentStage.mcCompanyId;
    }

    const operation = this.modalMode === 'create'
      ? this.stageService.createStage(stageData)
      : this.stageService.updateStage(this.currentStage.id, stageData);

    this.subscriptions.add(
      operation.subscribe({
        next: () => {
          this.saving = false;
          this.showToast(
            this.modalMode === 'create' ? 'Stage created successfully' : 'Stage updated successfully',
            'success'
          );
          this.closeModal();
          // Force reload stages to ensure the list updates properly
          setTimeout(() => {
            this.loadStages();
            this.cdr.detectChanges(); // Force change detection
          }, 100); // Small delay to ensure modal closes first
        },
        error: (error) => {
          this.saving = false;
          this.showToast(error.error?.message || 'Failed to save stage', 'error');
        }
      })
    );
  }

  deleteStage() {
    if (!this.stageToDelete) return;
    
    this.deleting = true;
    this.subscriptions.add(
      this.stageService.deleteStage(this.stageToDelete._id).subscribe({
        next: () => {
          this.deleting = false;
          this.showDeleteModal = false;
          this.stageToDelete = null;
          this.showToast('Stage deleted successfully', 'success');
        },
        error: (error) => {
          this.deleting = false;
          this.showToast(error.error?.message || 'Failed to delete stage', 'error');
        }
      })
    );
  }

  openCreateModal() {
    this.currentStage = { name: '', description: '', order: null, mcCompanyId: '' };
    this.modalMode = 'create';
  }

  closeModal() {
    this.modalMode = null;
    this.currentStage = {};
  }

  trackByStageId(index: number, stage: ContactStage): string {
    return stage._id;
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }

  showToast(message: string, type: 'success' | 'error') {
    this.toastMessage = message;
    this.toastType = type;
    setTimeout(() => {
      this.toastMessage = '';
    }, 4000);
  }

  isSuperAdmin(): boolean {
    const currentUser = this.authService.currentUserValue;
    return currentUser?.role === UserRole.OWNER && 
           currentUser?.company_type === 'RC';
  }

  isRCSuperAdmin(): boolean {
    return this.isSuperAdmin();
  }

  loadMCCompanies() {
    if (!this.isSuperAdmin()) {
      return;
    }
    
    this.loadingCompanies = true;
    this.subscriptions.add(
      this.rcOversightService.getMCCompaniesDropdown().subscribe({
        next: (companies) => {
          this.mcCompanies = companies || [];
          this.loadingCompanies = false;
        },
        error: (error) => {
          console.error('Error loading MC companies:', error);
          this.mcCompanies = [];
          this.loadingCompanies = false;
        }
      })
    );
  }


  // Helper methods for role-based functionality
  isRCUser(): boolean {
    const currentUser = this.authService.currentUserValue;
    const isRC = currentUser?.role === UserRole.OWNER && 
                 currentUser?.company_type === 'RC';
    console.log('[StageManagement] isRCUser check - Role:', currentUser?.role, 'CompanyType:', currentUser?.company_type, 'Result:', isRC);
    return isRC;
  }
}